import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { OrgType, UserRole } from '@spi/shared';
import { useCreateUser, useApproveAccountRequest } from '../../../api/admin';

const createUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  role: z.enum([UserRole.DONOR, UserRole.RECIPIENT]),
  temporaryPassword: z.string().min(8, 'Password must be at least 8 characters'),
  organization: z.object({
    name: z.string().min(1, 'Organization name is required'),
    type: z.nativeEnum(OrgType),
    description: z.string().optional(),
    website: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    address: z.string().optional(),
    country: z.string().min(2, 'Country is required'),
    taxId: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
  }),
});

type CreateUserForm = z.infer<typeof createUserSchema>;

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  prefill?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: 'donor' | 'recipient';
    organization?: {
      name?: string;
      type?: string;
      country?: string;
    };
    accountRequestId?: number;
  };
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let pw = '';
  for (let i = 0; i < 12; i++) {
    pw += chars[Math.floor(Math.random() * chars.length)];
  }
  return pw;
}

export function CreateUserModal({ open, onClose, prefill }: CreateUserModalProps) {
  const createUser = useCreateUser();
  const approveRequest = useApproveAccountRequest();

  const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm<CreateUserForm>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      role: UserRole.DONOR,
      temporaryPassword: generatePassword(),
      organization: { type: OrgType.NONPROFIT },
    },
  });

  useEffect(() => {
    if (open) {
      const newPassword = generatePassword();
      if (prefill) {
        reset({
          firstName: prefill.firstName || '',
          lastName: prefill.lastName || '',
          email: prefill.email || '',
          role: (prefill.role as UserRole.DONOR | UserRole.RECIPIENT) || UserRole.DONOR,
          temporaryPassword: newPassword,
          organization: {
            name: prefill.organization?.name || '',
            type: (prefill.organization?.type as OrgType) || OrgType.NONPROFIT,
            country: prefill.organization?.country || '',
          },
        });
      } else {
        reset({
          firstName: '',
          lastName: '',
          email: '',
          role: UserRole.DONOR,
          temporaryPassword: newPassword,
          organization: { name: '', type: OrgType.NONPROFIT, country: '' },
        });
      }
    }
  }, [open, prefill, reset]);

  const onSubmit = async (data: CreateUserForm) => {
    try {
      await createUser.mutateAsync(data);
      if (prefill?.accountRequestId) {
        await approveRequest.mutateAsync(prefill.accountRequestId);
      }
      toast.success('User created — welcome email sent');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-spi-charcoal mb-4">Create User Account</h3>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Role */}
          <div>
            <label className="input-label">Role</label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input {...register('role')} type="radio" value={UserRole.DONOR} className="accent-spi-green" />
                <span className="text-sm">Donor</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input {...register('role')} type="radio" value={UserRole.RECIPIENT} className="accent-spi-green" />
                <span className="text-sm">Recipient</span>
              </label>
            </div>
          </div>

          {/* Personal info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">First Name</label>
              <input {...register('firstName')} type="text" className="input" />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
            </div>
            <div>
              <label className="input-label">Last Name</label>
              <input {...register('lastName')} type="text" className="input" />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">Email</label>
              <input {...register('email')} type="email" className="input" />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>
            <div>
              <label className="input-label">Phone <span className="text-spi-slate font-normal">(optional)</span></label>
              <input {...register('phone')} type="text" className="input" />
            </div>
          </div>

          {/* Temporary password */}
          <div>
            <label className="input-label">Temporary Password</label>
            <div className="flex gap-2">
              <input {...register('temporaryPassword')} type="text" className="input flex-1" />
              <button
                type="button"
                onClick={() => setValue('temporaryPassword', generatePassword())}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-spi-seed text-spi-charcoal hover:bg-spi-parchment transition-colors whitespace-nowrap"
              >
                Generate
              </button>
            </div>
            {errors.temporaryPassword && <p className="text-red-500 text-xs mt-1">{errors.temporaryPassword.message}</p>}
          </div>

          {/* Organization */}
          <div className="border-t border-spi-parchment pt-4 mt-4">
            <h4 className="text-sm font-semibold text-spi-charcoal mb-3">Organization</h4>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">Name</label>
                  <input {...register('organization.name')} type="text" className="input" />
                  {errors.organization?.name && <p className="text-red-500 text-xs mt-1">{errors.organization.name.message}</p>}
                </div>
                <div>
                  <label className="input-label">Type</label>
                  <select {...register('organization.type')} className="input">
                    <option value={OrgType.COMPANY}>Company</option>
                    <option value={OrgType.FOUNDATION}>Foundation</option>
                    <option value={OrgType.NONPROFIT}>Non-Profit</option>
                    <option value={OrgType.SCHOOL}>School</option>
                    <option value={OrgType.GOVERNMENT}>Government</option>
                    <option value={OrgType.OTHER}>Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">Country</label>
                  <input {...register('organization.country')} type="text" className="input" />
                  {errors.organization?.country && <p className="text-red-500 text-xs mt-1">{errors.organization.country.message}</p>}
                </div>
                <div>
                  <label className="input-label">Tax ID <span className="text-spi-slate font-normal">(optional)</span></label>
                  <input {...register('organization.taxId')} type="text" className="input" />
                </div>
              </div>

              <div>
                <label className="input-label">Website <span className="text-spi-slate font-normal">(optional)</span></label>
                <input {...register('organization.website')} type="text" className="input" placeholder="https://..." />
              </div>

              <div>
                <label className="input-label">Address <span className="text-spi-slate font-normal">(optional)</span></label>
                <input {...register('organization.address')} type="text" className="input" />
              </div>

              <div>
                <label className="input-label">Description <span className="text-spi-slate font-normal">(optional)</span></label>
                <textarea {...register('organization.description')} className="input min-h-[60px]" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-spi-slate hover:bg-spi-seed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createUser.isPending}
              className="btn btn-primary"
            >
              {createUser.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
